import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { mapCommentResponseToComment } from '@components-clean/core/comments-side-bar/utilities';
import { CommentEntityType, SelectorData } from 'types/graphql-global-types';
import { Logger } from '@utils/logger';
import {
    CREATE_COMMENT,
    UPDATE_COMMENT,
    DELETE_COMMENT,
    OPEN_COMMENT,
    CLOSE_COMMENT,
    CREATE_COMMENT_REPLY,
    DELETE_COMMENT_REPLY,
    UPDATE_COMMENT_REPLY,
} from '@queries/common';
import {
    createComment as createCommentReturnType,
    createCommentVariables,
    updateComment as updateCommentReturnType,
    updateCommentVariables,
    deleteComment as deleteCommentReturnType,
    deleteCommentVariables,
    openComment as openCommentReturnType,
    openCommentVariables,
    closeComment as closeCommentReturnType,
    closeCommentVariables,
    createCommentReply as createCommentReplyReturnType,
    createCommentReplyVariables,
    deleteCommentReply as deleteCommentReplyReturnType,
    deleteCommentReplyVariables,
    updateCommentReply as updateCommentReplyReturnType,
    updateCommentReplyVariables,
} from '@queries/generated/common.generated';

const useCommentMutations = (parentId: string | undefined, entityType: CommentEntityType) => {
    const apolloClient = useApolloClient();

    const createComment = async (content: string) => {
        try {
            const result = await apolloClient.mutate<createCommentReturnType, createCommentVariables>({
                mutation: CREATE_COMMENT,
                variables: {
                    commentInput: {
                        parentId,
                        entityType,
                        content,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            if (!result.data!.createComment) {
                return null;
            }
            return mapCommentResponseToComment(result.data!.createComment!);
        } catch (error) {
            Logger.error('Error creating comment:', error);
            return null;
        }
    };

    const openComment = useCallback(async (commentId: string, replySelectors: SelectorData[]) => {
        try {
            const result = await apolloClient.mutate<openCommentReturnType, openCommentVariables>({
                mutation: OPEN_COMMENT,
                variables: {
                    commentInput: {
                        id: commentId,
                        entityType,
                        replySelectors,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            if (!result.data?.openComment) {
                return null;
            }
            return result.data.openComment;
        } catch (error) {
            Logger.error('Error opening comment:', error);
            return null;
        }
    }, []);

    const createCommentReply = async (commentId: string, content: string, replySelectors: SelectorData[]) => {
        try {
            const result = await apolloClient.mutate<createCommentReplyReturnType, createCommentReplyVariables>({
                mutation: CREATE_COMMENT_REPLY,
                variables: {
                    replyInput: {
                        parentCommentId: commentId,
                        entityType,
                        content,
                        replySelectors,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            if (!result.data?.createCommentReply) {
                return null;
            }
            return result.data.createCommentReply;
        } catch (error) {
            Logger.error('Error creating comment:', error);
            return null;
        }
    };

    const closeComment = async (commentId: string, replySelectors: SelectorData[]) => {
        try {
            const result = await apolloClient.mutate<closeCommentReturnType, closeCommentVariables>({
                mutation: CLOSE_COMMENT,
                variables: {
                    commentInput: {
                        id: commentId,
                        entityType,
                        replySelectors,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            if (!result.data?.closeComment) {
                return null;
            }
            return result.data.closeComment;
        } catch (error) {
            Logger.error('Error closing comment:', error);
            return null;
        }
    };

    const deleteComment = async (commentId: string) => {
        try {
            const result = await apolloClient.mutate<deleteCommentReturnType, deleteCommentVariables>({
                mutation: DELETE_COMMENT,
                variables: {
                    commentInput: {
                        id: commentId,
                        entityType,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            return result.data?.deleteComment;
        } catch (error) {
            Logger.error('Error deleting comment:', error);
            return false;
        }
    };

    const deleteCommentReply = async (replyId: string, replySelectors: SelectorData[]) => {
        try {
            const result = await apolloClient.mutate<deleteCommentReplyReturnType, deleteCommentReplyVariables>({
                mutation: DELETE_COMMENT_REPLY,
                variables: {
                    replyInput: {
                        id: replyId,
                        entityType,
                        replySelectors,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            if (!result.data?.deleteCommentReply) {
                return null;
            }
            return result.data.deleteCommentReply;
        } catch (error) {
            Logger.error('Error deleting comment:', error);
            return null;
        }
    };

    const updateComment = async (commentId: string, content: string, replySelectors: SelectorData[]) => {
        try {
            const result = await apolloClient.mutate<updateCommentReturnType, updateCommentVariables>({
                mutation: UPDATE_COMMENT,
                variables: {
                    commentInput: {
                        id: commentId,
                        entityType,
                        content,
                        replySelectors,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            if (!result.data?.updateComment) {
                return null;
            }
            return result.data.updateComment;
        } catch (error) {
            Logger.error('Error updating comment:', error);
            return null;
        }
    };

    const updateCommentReply = async (replyId: string, content: string, replySelectors: SelectorData[]) => {
        try {
            const result = await apolloClient.mutate<updateCommentReplyReturnType, updateCommentReplyVariables>({
                mutation: UPDATE_COMMENT_REPLY,
                variables: {
                    replyInput: {
                        id: replyId,
                        entityType,
                        content,
                        replySelectors,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            if (!result.data?.updateCommentReply) {
                return null;
            }
            return result.data.updateCommentReply;
        } catch (error) {
            Logger.error('Error updating comment reply:', error);
            return null;
        }
    };

    return {
        createComment,
        openComment,
        updateComment,
        deleteComment,
        closeComment,
        deleteCommentReply,
        createCommentReply,
        updateCommentReply,
    };
};

export default useCommentMutations;
